import { getFunctions, httpsCallable } from 'firebase/functions';
import { doc, getDoc, updateDoc, setDoc } from 'firebase/firestore';
import { db, auth } from '../firebase';

const functions = getFunctions();

const base64UrlEncode = (inputBytes) => {
  let binary = '';
  const bytes = inputBytes instanceof Uint8Array ? inputBytes : new Uint8Array(inputBytes);
  for (let i = 0; i < bytes.length; i += 1) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
};

const createPkceVerifier = () => {
  const randomBytes = new Uint8Array(32);
  window.crypto.getRandomValues(randomBytes);
  return base64UrlEncode(randomBytes);
};

const createPkceChallenge = async (verifier) => {
  const encoded = new TextEncoder().encode(verifier);
  const digest = await window.crypto.subtle.digest('SHA-256', encoded);
  return base64UrlEncode(new Uint8Array(digest));
};

// Integration configuration (same as integrationService.js)
const INTEGRATION_CONFIG = {
  googleCalendar: {
    name: 'Google Calendar',
    authUrl: 'https://accounts.google.com/o/oauth2/v2/auth',
    clientId: process.env.REACT_APP_GOOGLE_CLIENT_ID,
    scope: 'https://www.googleapis.com/auth/calendar https://www.googleapis.com/auth/calendar.events https://www.googleapis.com/auth/userinfo.email https://www.googleapis.com/auth/userinfo.profile',
    redirectUri: `${window.location.origin}/auth/google/callback`
  },
  gmail: {
    name: 'Gmail',
    authUrl: 'https://accounts.google.com/o/oauth2/v2/auth',
    clientId: process.env.REACT_APP_GOOGLE_CLIENT_ID,
    scope: 'https://www.googleapis.com/auth/gmail.readonly https://www.googleapis.com/auth/contacts.readonly https://www.googleapis.com/auth/userinfo.email https://www.googleapis.com/auth/userinfo.profile',
    redirectUri: `${window.location.origin}/auth/google/callback`
  },
  calendly: {
    name: 'Calendly',
    authUrl: 'https://auth.calendly.com/oauth/authorize',
    clientId: process.env.REACT_APP_CALENDLY_CLIENT_ID,
    scope: 'default',
    redirectUri: `${window.location.origin}/auth/calendly/callback`
  },
  fathom: {
    name: 'Fathom',
    authUrl: 'https://app.fathom.video/oauth/authorize',
    clientId: process.env.REACT_APP_FATHOM_CLIENT_ID,
    scope: 'meetings:read meetings:write',
    redirectUri: `${window.location.origin}/auth/fathom/callback`
  },
  quickbooks: {
    name: 'QuickBooks',
    authUrl: 'https://appcenter.intuit.com/connect/oauth2',
    clientId: process.env.REACT_APP_QUICKBOOKS_CLIENT_ID,
    scope: 'com.intuit.quickbooks.accounting',
    redirectUri: `${window.location.origin}/auth/quickbooks/callback`
  },
  jobtread: {
    name: 'JobTread',
    authType: 'grant', // JobTread uses grant-based authentication, not OAuth
    apiUrl: 'https://api.jobtread.com/pave',
    grantKeyField: 'grantKey'
  }
};

export const initiateUserIntegrationConnection = async (integrationKey) => {
  const config = INTEGRATION_CONFIG[integrationKey];
  
  if (!config) {
    throw new Error(`Unknown integration: ${integrationKey}`);
  }

  // Handle JobTread grant-based authentication differently
  if (integrationKey === 'jobtread') {
    // For JobTread, we need to prompt for grant key instead of OAuth
    const grantKey = prompt('Please enter your JobTread Grant Key:');
    if (!grantKey) {
      throw new Error('Grant key is required for JobTread integration');
    }
    
    if (!auth.currentUser) {
      throw new Error('User not authenticated');
    }

    // The server validates and stores the key, then marks the profile connected
    await saveJobTreadGrant(grantKey.trim());
    return;
  }

  if (!config.clientId) {
    throw new Error(`Client ID not configured for ${integrationKey}`);
  }

  // Build OAuth URL with user ID in state
  const user = auth.currentUser;
  if (!user) {
    throw new Error('User not authenticated');
  }

  const params = new URLSearchParams({
    client_id: config.clientId,
    redirect_uri: config.redirectUri,
    scope: config.scope,
    response_type: 'code',
    state: `${integrationKey}:${user.uid}` // Pass integration key and user ID as state
  });

  if (integrationKey === 'calendly') {
    const codeVerifier = createPkceVerifier();
    const codeChallenge = await createPkceChallenge(codeVerifier);
    sessionStorage.setItem(`oauth_pkce_calendly_${user.uid}`, codeVerifier);
    sessionStorage.setItem(`oauth_calendly_client_id_${user.uid}`, config.clientId);
    params.append('code_challenge', codeChallenge);
    params.append('code_challenge_method', 'S256');
  }

  // Add access_type=offline for Google integrations to get refresh tokens
  if (integrationKey === 'googleCalendar' || integrationKey === 'gmail') {
    params.append('access_type', 'offline');
    params.append('prompt', 'consent'); // Force consent screen to ensure refresh token
  }

  const authUrl = `${config.authUrl}?${params.toString()}`;
  
  // Redirect to OAuth provider
  window.location.href = authUrl;
};

export const handleUserIntegrationCallback = async (
  integrationKey,
  code,
  userId,
  redirectUri = null,
  codeVerifier = null,
  oauthClientId = null
) => {
  try {
    const handleCallback = httpsCallable(functions, 'handleUserIntegrationCallback');
    
    // If redirectUri not provided, construct it from current origin
    const finalRedirectUri = redirectUri || `${window.location.origin}/auth/google/callback`;
    
    const result = await handleCallback({
      integrationType: integrationKey,
      code: code,
      userId: userId,
      redirectUri: finalRedirectUri,
      ...(codeVerifier ? { codeVerifier } : {}),
      ...(oauthClientId ? { oauthClientId } : {}),
    });

    return result.data;
  } catch (error) {
    console.error('Error handling user integration callback:', error);
    throw error;
  }
};

export const getUserCollection = async (userId) => {
  const internalDoc = await getDoc(doc(db, "internal_users", userId));
  if (internalDoc.exists()) return "internal_users";
  return "users";
};

export const getUserIntegrationsStatus = async (userId) => {
  try {
    const internalDoc = await getDoc(doc(db, "internal_users", userId));
    if (internalDoc.exists()) {
      const data = internalDoc.data();
      return data.integrations || {};
    }
    const userDoc = await getDoc(doc(db, "users", userId));
    if (userDoc.exists()) {
      const data = userDoc.data();
      return data.integrations || {};
    }
    return {};
  } catch (error) {
    console.error('Error getting user integrations status:', error);
    throw error;
  }
};

/**
 * Write only the non-secret connection flags for an integration. Tokens and grant
 * keys live in server-only storage and are written by Cloud Functions
 * (OAuth callback, saveJobTreadGrant), never from the browser.
 */
export const updateUserIntegrationStatus = async (userId, integrationKey, status) => {
  try {
    const collectionName = await getUserCollection(userId);
    const userDoc = doc(db, collectionName, userId);
    const docSnap = await getDoc(userDoc);

    const updateData = {
      [`integrations.${integrationKey}.connected`]: status,
      [`integrations.${integrationKey}.connectedAt`]: status ? new Date().toISOString() : null,
      [`integrations.${integrationKey}.needsReauth`]: false,
      lastUpdated: new Date().toISOString()
    };

    if (docSnap.exists()) {
      await updateDoc(userDoc, updateData);
    } else {
      await setDoc(userDoc, {
        integrations: {
          [integrationKey]: {
            connected: status,
            connectedAt: status ? new Date().toISOString() : null,
            needsReauth: false
          }
        },
        lastUpdated: new Date().toISOString()
      }, { merge: true });
    }
    return true;
  } catch (error) {
    console.error('Error updating user integration status:', error);
    throw error;
  }
};

/**
 * Mark an integration as needing re-authorization.
 * Used when the provider has told us the grant is dead (e.g. an expired Google refresh
 * token). The `connected` flag on the profile is the source of truth everywhere else,
 * so it has to stop claiming "connected" — otherwise My Profile and the pages that
 * actually call the API disagree. Stored tokens are left alone; reconnecting overwrites
 * them via the OAuth callback.
 */
export const markUserIntegrationNeedsReauth = async (userId, integrationKey) => {
  try {
    const collectionName = await getUserCollection(userId);
    const userDoc = doc(db, collectionName, userId);
    const docSnap = await getDoc(userDoc);

    if (!docSnap.exists()) return false;

    await updateDoc(userDoc, {
      [`integrations.${integrationKey}.connected`]: false,
      [`integrations.${integrationKey}.needsReauth`]: true,
      [`integrations.${integrationKey}.needsReauthAt`]: new Date().toISOString(),
      lastUpdated: new Date().toISOString()
    });
    return true;
  } catch (error) {
    console.error('Error marking integration as needing reauth:', error);
    return false;
  }
};

/**
 * Validate and store a JobTread grant key server-side, and mark the profile's
 * integrations.jobtread.connected flag. Staff may pass targetUserId to save a key
 * for a portal user; omit it to save for the signed-in user.
 */
export const saveJobTreadGrant = async (grantKey, targetUserId) => {
  const callable = httpsCallable(functions, 'saveJobTreadGrant');
  const { data } = await callable({
    grantKey,
    ...(targetUserId ? { targetUserId } : {})
  });
  if (!data?.success) {
    throw new Error(data?.error || 'Failed to save the JobTread grant key');
  }
  return true;
};

/**
 * Disconnect an integration: the server deletes the stored grant/tokens and sets
 * connected=false. userId is the profile being disconnected; when it isn't the
 * signed-in user it's sent as targetUserId (staff only, enforced server-side).
 */
export const disconnectUserIntegration = async (userId, integrationKey) => {
  try {
    const callable = httpsCallable(functions, 'disconnectUserIntegration');
    const isSelf = !userId || userId === auth.currentUser?.uid;
    await callable({
      key: integrationKey,
      ...(isSelf ? {} : { targetUserId: userId })
    });
    return true;
  } catch (error) {
    console.error('Error disconnecting user integration:', error);
    throw error;
  }
};

export const getIntegrationDisplayName = (integrationKey) => {
  return INTEGRATION_CONFIG[integrationKey]?.name || integrationKey;
};

export const isIntegrationConfigured = (integrationKey) => {
  return !!INTEGRATION_CONFIG[integrationKey]?.clientId;
};
